import * as HighStock from 'highcharts/highstock';
import { HighChartBuilder } from "../builders/highchart.builder";
import { FormatHelpers } from "../helpers/format-helpers";
import { IndicatorHelpers } from "../helpers/indicator-helper";
import { IChartContainer } from '../models/base/chart-container.interface';
import { IComponent } from "./component.interface";
import { StringHelpers } from '../helpers/string-helpers';

export class IndicatorLastPriceComponent implements IComponent {
    public id = StringHelpers.newGuid();;

    private element: HTMLElement;
    private base: IChartContainer;

    private resizeTimmer = null as any;
    private colors: any = [];
    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.base = base;
        this.onResize();
        
        this.colors = HighStock.getOptions().colors;

        this.base.tiChartSeries.subscribeExtremeChange(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.setLastPriceLabelPosition();
            }, 250);
        });

        this.base.tiChartSeries.subscribeRedrawChange(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.setLastPriceLabelPosition();
            }, 250);
        });
    }
    
    public hide() {
        this.element.querySelectorAll<HTMLElement>('.indicator-last-prices .last-price-wrapper').forEach((el: HTMLElement) => {
            el.classList.add('hidden');
        });
    }

    public createLastPriceForIndicators() {
        if (!this.base.tiChartSeries || !this.base.tiChartSeries.lastDataPoint) {
            return;
        }

        const template = `<div class="last-price-wrapper {class}">
            <div class="last-price">
                <p>{value}</p>
            </div>
        </div>
        <style>
            .last-price-wrapper.{class} {
                background-color: var(--{class}-bg-color);
            } 
            .last-price-wrapper.{class} .last-price:before {
                border-color: transparent var(--{class}-bg-color) transparent transparent;
            }
        </style>`;
        const root = document.documentElement;

        this.element.innerHTML = '';

        // if (this.base.tiChartSeries.lastIndicatorXValue == null) {
        //     return;
        // }

        const xValue = this.base.tiChartSeries.lastIndicatorXValue as number;
        const indicatorOptions = this.base.tiChartSeries.getAllActiveIndicatorOptions().filter(x => x.chartAreaName != HighChartBuilder.mainChartAreaName);
        for (const indicatorOption of indicatorOptions) {
            const indicatorData = IndicatorHelpers.findIndicatorPoints(this.base.tiChartSeries.tiIndicatorsData, indicatorOption, xValue);
            let name = indicatorOption.getClassName();
            if (indicatorData?.length) {
                const html = template.split('{class}').join(name).replace('{value}', FormatHelpers.numberToString(indicatorData[0].value));
                root.style.setProperty('--' + name + '-bg-color', indicatorData[0].color);
                this.element.innerHTML += html;
            } else if (indicatorOption.highChartIndicator && indicatorOption.chartAreaName.indexOf('highchart') == -1) {
                const ind = this.base.tiChartSeries.tiIndicatorsData.find(x => x.indicatorType == indicatorOption.value && x.chartAreaName == indicatorOption.chartAreaName);
                
                name = ind?.getClassName() ?? name;
                
                const html =  template.split('{class}').join(name).replace('{value}', FormatHelpers.numberToString(0));
                root.style.setProperty('--' + name + '-bg-color', ind.color ?? 'rgb(255, 109, 0)');
                this.element.innerHTML += html;
            } else if (indicatorOption.chartAreaName.indexOf('highchart') > -1) {
                const html = template.split('{class}').join(name).replace('{value}', FormatHelpers.numberToString(0));

                let color = 'red';

                if (indicatorOption.chartAreaName == 'highchart-stochastic') {
                    color = 'rgb(255, 109, 0)';
                } else {
                    const series: any = this.base.tiChartSeries.highStockChart.series.find((x: any) => x.name == indicatorOption.chartAreaName);
                    const colorInd = series?.colorIndex ?? -1;
                    if (colorInd > -1 && this.colors?.length) {
                        color = this.colors[colorInd];
                    }
                }

                root.style.setProperty('--' + name + '-bg-color', color);
                this.element.innerHTML += html;
            }
        }
    }

    public setLastPriceLabelPosition() {
        if (!this.base.tiChartSeries || !this.base.tiChartSeries.lastDataPoint) {
            return;
        }
        
        const root = document.documentElement;
        // if (this.base.tiChartSeries.lastIndicatorXValue == null) {
        //     return;
        // }

        const indicatorOptions = this.base.tiChartSeries.getAllActiveIndicatorOptions().filter(x => x.chartAreaName != HighChartBuilder.mainChartAreaName);
        const lastPriceWrappers = Array.from(this.element.querySelectorAll<HTMLElement>('.indicator-last-prices .last-price-wrapper'));
        const xValue = this.base.tiChartSeries.lastIndicatorXValue as number;
        for (let i = 0; i < indicatorOptions.length; i++) {
            const indicatorOption = indicatorOptions[i];
            const lastPriceWrapper = lastPriceWrappers[i];
            const className = indicatorOption.getClassName();
            if (!lastPriceWrapper) {
                continue;
            }

            this.base.tiChartSeries.element.querySelectorAll('.highcharts-crosshair.highcharts-crosshair-thin.last-price-indicator-' + className).forEach(x => {
                x.setAttribute('style', 'visibility: hidden; opacity: 0;');
            });
            
            let close = 0;
            if (indicatorOption) {
                if (indicatorOption.highChartIndicator) {
                    const cfg = IndicatorHelpers.getIndicatorHighChartConfig(indicatorOption.value);
                    const series: any = this.base.tiChartSeries.highStockChart.series.find((x: any) => x.name == cfg.series?.[0].name);
                    const indicatorLastPrice = series.yData[series.yData.length - 1];
                    if (indicatorLastPrice instanceof Array && indicatorLastPrice.length > 0) {
                        close = indicatorLastPrice[0];
                    } else {
                        close = indicatorLastPrice;
                    }
                } else {
                    if (xValue == null) {
                        return;
                    }
            
                    const indicatorData = IndicatorHelpers.findIndicatorPoints(this.base.tiChartSeries.tiIndicatorsData, indicatorOption, xValue);
                    if (indicatorData?.length) {
                        close = indicatorData[0].value;
                        const name = indicatorData[0].name.split(' ').join('').replace(/[^\w\s]/gi, '').toLowerCase();
                        root.style.setProperty('--' + name + '-bg-color', indicatorData[0].color);
                    }
                }
            }

            const yAxisId = indicatorOption.chartAreaName + '-y-axis';
            const yAxis: any = this.base.tiChartSeries.highStockChart?.get(yAxisId);
            if (!yAxis) {
                continue;
            }

            const currentValue =  yAxis.toPixels(close) ?? 0;
            if (isNaN(currentValue)) {
                lastPriceWrapper.classList.add('hidden');
                continue;
            }
            
            const myHeight = 8;
            let top = currentValue - myHeight;
            if (close >= yAxis.max) {
                const valueTop =  yAxis.toPixels(yAxis.max) ?? 0;
                top = valueTop - myHeight;
            } else if (close <= yAxis.min) {
                const valueBottom =  yAxis.toPixels(yAxis.min) ?? 0;
                top = valueBottom + myHeight;
            }

            if (top + 16 > this.base.tiChartSeries.chartElement.clientHeight) {
                top = this.base.tiChartSeries.chartElement.clientHeight - 16;
            }
    
            const pElement = lastPriceWrapper.findFirst('p');
            if (pElement) {
                pElement.innerHTML = FormatHelpers.numberToString(close);
            }

            lastPriceWrapper.style.top = top + 'px';
            lastPriceWrapper.classList.remove('hidden');
        }
    }

    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.setLastPriceLabelPosition();
            }, 250);
        });
    }
}
